import { useQuery } from "@tanstack/react-query";
import {
  companyApi,
  branchApi,
  departmentApi,
  roleApi,
  jobTitleApi,
  clientApi,
} from "../organizationApi";
import { queryKeys, LOOKUP_STALE_TIME } from "./queryKeys";
import useInvalidatingMutation from "./useInvalidatingMutation";

const lookup = (queryKey, queryFn, enabled = true) => ({
  queryKey,
  queryFn,
  enabled,
  staleTime: LOOKUP_STALE_TIME,
});

/* ---------- Companies ---------- */
export const useCompanies = () =>
  useQuery(
    lookup(queryKeys.companies.descendants(), companyApi.getDescendants),
  );

export const useCompanyHierarchy = (companyId) =>
  useQuery(
    lookup(
      queryKeys.companies.hierarchy(companyId),
      () => companyApi.getHierarchy(companyId),
      !!companyId,
    ),
  );

// Companies screen
const COMPANY_KEYS = [queryKeys.companies.all, ["orgChart"]];
export const useCompanyTree = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: ["orgChart", "companies"],
    queryFn: companyApi.getTree,
    enabled,
  });
export const useCompanyDetails = (id) =>
  useQuery({
    queryKey: [...queryKeys.companies.all, "details", id],
    queryFn: () => companyApi.getDetails(id),
    enabled: !!id,
    retry: false,
  });
export const useCompanyAddresses = (id) =>
  useQuery({
    queryKey: [...queryKeys.companies.all, "addresses", id],
    queryFn: () => companyApi.getAddresses(id),
    enabled: !!id,
  });
export const useSaveCompany = () =>
  useInvalidatingMutation(
    ({ id, ...data }) =>
      id ? companyApi.update(id, data) : companyApi.create(data),
    COMPANY_KEYS,
  );
export const useDeleteCompany = () =>
  useInvalidatingMutation((id) => companyApi.remove(id), COMPANY_KEYS);
export const useSaveCompanyDetails = () =>
  useInvalidatingMutation(
    ({ id, ...data }) => companyApi.saveDetails(id, data),
    COMPANY_KEYS,
  );
export const useSaveCompanyAddress = () =>
  useInvalidatingMutation(
    ({ companyId, addressId, ...data }) =>
      addressId
        ? companyApi.updateAddress(addressId, data)
        : companyApi.addAddress(companyId, data),
    [queryKeys.companies.all],
  );
export const useDeleteCompanyAddress = () =>
  useInvalidatingMutation(
    (addressId) => companyApi.removeAddress(addressId),
    [queryKeys.companies.all],
  );

/* ---------- Branches ---------- */
export const useBranchesByCompany = (companyId) =>
  useQuery(
    lookup(
      queryKeys.branches.byCompany(companyId),
      () => branchApi.getByCompany(companyId),
      !!companyId,
    ),
  );

/** Every visible branch (used when "All companies" is selected). */
export const useAllBranches = (enabled = true) =>
  useQuery(
    lookup(
      [...queryKeys.branches.all, "every"],
      () => branchApi.getAll(),
      enabled,
    ),
  );

export const useBranchHierarchy = (branchId) =>
  useQuery(
    lookup(
      queryKeys.branches.hierarchy(branchId),
      () => branchApi.getHierarchy(branchId),
      !!branchId,
    ),
  );

export const useBranch = (id) =>
  useQuery({
    queryKey: [...queryKeys.branches.all, "one", id],
    queryFn: () => branchApi.getById(id),
    enabled: !!id,
  });

export const useCreateBranch = () =>
  useInvalidatingMutation(
    (data) => branchApi.create(data),
    [queryKeys.branches.all, ["orgChart"]],
  );

export const useDeleteBranch = () =>
  useInvalidatingMutation(
    (id) => branchApi.remove(id),
    [queryKeys.branches.all, ["orgChart"], queryKeys.companies.all],
  );

export const useSetBranchLocation = () =>
  useInvalidatingMutation(
    ({ id, ...body }) => branchApi.setLocation(id, body),
    [queryKeys.branches.all, ["attendanceLocations"]],
  );

export const useUpdateBranch = () =>
  useInvalidatingMutation(
    ({ id, data }) => branchApi.update(id, data),
    [queryKeys.branches.all, ["orgChart"]],
  );

/* ---------- Departments ---------- */
export const useDepartments = (companyId, { enabled = true, active } = {}) =>
  useQuery(
    lookup(
      [...queryKeys.departments.list(companyId), active ?? "any"],
      () => departmentApi.getAll(companyId, { active }),
      enabled,
    ),
  );

export const useDeleteDepartment = () =>
  useInvalidatingMutation(
    (id) => departmentApi.remove(id),
    [queryKeys.departments.all],
  );

export const useCreateDepartment = () =>
  useInvalidatingMutation(
    (data) => departmentApi.create(data),
    [queryKeys.departments.all],
  );

export const useUpdateDepartment = () =>
  useInvalidatingMutation(
    ({ id, data }) => departmentApi.update(id, data),
    [queryKeys.departments.all],
  );

export const useToggleDepartmentStatus = () =>
  useInvalidatingMutation(
    ({ id, currentStatus }) => departmentApi.toggleStatus(id, currentStatus),
    [queryKeys.departments.all],
  );

/* ---------- Roles ---------- */
export const useRoles = () =>
  useQuery(lookup(queryKeys.roles.all, () => roleApi.getAll()));
/** Shared roles + one company's roles (server-filtered). */
export const useCompanyRoles = (companyId) =>
  useQuery(
    lookup(
      [...queryKeys.roles.all, "company", companyId],
      () => roleApi.getAll(companyId),
      !!companyId,
    ),
  );

export const useCreateRole = () =>
  useInvalidatingMutation(
    (data) => roleApi.create(data),
    [queryKeys.roles.all],
  );

export const useUpdateRole = () =>
  useInvalidatingMutation(
    ({ id, data }) => roleApi.update(id, data),
    [queryKeys.roles.all],
  );

export const useDeleteRole = () =>
  useInvalidatingMutation((id) => roleApi.remove(id), [queryKeys.roles.all]);

/* ---------- Job titles ---------- */
export const useJobTitles = () =>
  useQuery(lookup(queryKeys.jobTitles.all, jobTitleApi.getAll));

export const useCreateJobTitle = () =>
  useInvalidatingMutation(
    (data) => jobTitleApi.create(data),
    [queryKeys.jobTitles.all],
  );

export const useUpdateJobTitle = () =>
  useInvalidatingMutation(
    ({ id, data }) => jobTitleApi.update(id, data),
    [queryKeys.jobTitles.all],
  );

export const useToggleJobTitleStatus = () =>
  useInvalidatingMutation(
    (id) => jobTitleApi.toggleStatus(id),
    [queryKeys.jobTitles.all],
  );

/* ---------- Payroll groups (clients) ---------- */
export const useClients = () =>
  useQuery(lookup(queryKeys.clients.all, clientApi.getAll));

export const useCreateClient = () =>
  useInvalidatingMutation(
    (data) => clientApi.create(data),
    [queryKeys.clients.all],
  );

export const useUpdateClient = () =>
  useInvalidatingMutation(
    ({ id, data }) => clientApi.update(id, data),
    [queryKeys.clients.all],
  );

export const useToggleClientStatus = () =>
  useInvalidatingMutation(
    ({ id, currentStatus }) => clientApi.toggleStatus(id, currentStatus),
    [queryKeys.clients.all],
  );
